import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Edit, Trash2 } from "lucide-react";
import ModalWrapper from "../../../../Modal";
import AddLeaves from "../../../../modals/addLeaves";
import EditLeaves from "../../../../modals/editLeaves";
import { getRequest, deleteRequest } from "../../../../service";
import { getEmployeeData } from "../../../../utils/localStorage";

const AddAnnualLeave = () => {
    const [annualLeaves, setAnnualLeaves] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);

    // Permission checks
    const employeeData = getEmployeeData();
    const userRole = employeeData?.role;
    const annualLeavePermissions = employeeData?.designation?.permissions?.Leaves?.['Add Leaves'] || [];
    
    const canView = userRole === "companyadmin" || annualLeavePermissions.includes("view");
    const canAdd = userRole === "companyadmin" || annualLeavePermissions.includes("add");
    const canEdit = userRole === "companyadmin" || annualLeavePermissions.includes("edit");
    const canDelete = userRole === "companyadmin" || annualLeavePermissions.includes("delete");

    // Fetch leaves data from API
    const fetchLeaves = async () => {
        try {
            setLoading(true);
            if (!canView) {
                setAnnualLeaves([]);
                return;
            }
            const response = await getRequest('/api/leaves');
            if (response?.leaveConfigs) {
                setAnnualLeaves(response.leaveConfigs || []);
            } else {
                message.error('Failed to fetch leaves data');
            }
        } catch (error) {
            console.error('Error fetching leaves:', error);
            message.error('Error fetching leaves data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeaves();
    }, [canView]);

    const handleAddLeave = () => {
        if (!canAdd) {
            message.warning("You don't have permission to add annual leaves.");
            return;
        }
        setAddModalOpen(true);
    };

    const handleEditLeave = (record) => {
        if (!canEdit) {
            message.warning("You don't have permission to edit annual leaves.");
            return;
        }
        setSelectedLeave(record);
        setEditModalOpen(true);
    };

    const handleDeleteLeave = async (record) => {
        if (!canDelete) {
            message.warning("You don't have permission to delete annual leaves.");
            return;
        }

        Modal.confirm({
            title: 'Are you sure you want to delete this leave?',
            content: `This will permanently delete the leave "${record.title}" and all its associated data.`,
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'No, Cancel',
            onOk: async () => {
                try {
                    const response = await deleteRequest(`/api/leaves?configId=${record._id}`);
                    if (response.message === "Leaves deleted successfully") {
                        message.success('Leave deleted successfully');
                        fetchLeaves();
                    } else {
                        message.error(response.message || 'Failed to delete leave');
                    }
                } catch (error) {
                    console.error('Error deleting leave:', error);
                    message.error('Failed to delete leave');
                }
            },
        });
    };

    const closeAddModal = () => {
        setAddModalOpen(false);
        // Refresh data after adding
        fetchLeaves();
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setSelectedLeave(null);
        // Refresh data after editing
        fetchLeaves();
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => type?.charAt(0).toUpperCase() + type?.slice(1) || '-',
        },
        {
            title: 'Duration',
            dataIndex: 'leaveDuration',
            key: 'leaveDuration',
        },
        {
            title: 'Number of Days',
            dataIndex: 'numberOfDays',
            key: 'numberOfDays',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        type="text" 
                        icon={<Edit className="w-4 h-4" />}
                        onClick={() => handleEditLeave(record)}
                        className={canEdit ? "text-blue-600 hover:text-blue-800" : "text-gray-400 cursor-not-allowed"}
                        disabled={!canEdit}
                        title={canEdit ? "Edit" : "You don't have permission to edit"}
                    />
                    <Button 
                        type="text" 
                        icon={<Trash2 className="w-4 h-4" />}
                        onClick={() => handleDeleteLeave(record)}
                        className={canDelete ? "text-red-600 hover:text-red-800" : "text-gray-400 cursor-not-allowed"}
                        disabled={!canDelete}
                        title={canDelete ? "Delete" : "You don't have permission to delete"}
                    />
                </Space>
            ),
        },
    ];

    return (
        <>
            <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-semibold">Annual Leaves </h1>
                    <Button 
                        className='bg-primary text-white' 
                        type="primary" 
                        icon={<PlusOutlined />}
                        disabled={!canAdd}
                        onClick={() => canAdd ? handleAddLeave() : message.warning("You don't have permission to add annual leaves.")}
                    >
                        Add Leave
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={canView ? annualLeaves : []}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        total: canView ? annualLeaves.length : 0,
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                />
                {!canView && !loading && (
                    <div className="text-center text-gray-500 py-4">You don't have permission to view annual leaves.</div>
                )}
            </div>

            {/* Add Leave Modal */}
            <ModalWrapper
                title="Add New Leave"
                onRequestClose={closeAddModal}
                open={addModalOpen}
            >
                <AddLeaves onClose={closeAddModal} />
            </ModalWrapper>

            {/* Edit Leave Modal */}
            <ModalWrapper
                title="Edit Leave"
                onRequestClose={closeEditModal}
                open={editModalOpen}
            >
                <EditLeaves 
                    onClose={closeEditModal} 
                    leaveData={selectedLeave}
                />
            </ModalWrapper>
        </>
    );
}

export default AddAnnualLeave;
